import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NotesService, Note } from '../../services/notesService';
import PageHeader from '../common/PageHeader';
import './Notes.css';

interface NotesProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const Notes: React.FC<NotesProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);

  useEffect(() => {
    if (user) {
      loadNotes();
    }
  }, [user]);

  useEffect(() => {
    // Filter notes based on search query
    if (searchQuery.trim() === '') {
      setFilteredNotes(notes);
    } else {
      const filtered = notes.filter(note =>
        note.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredNotes(filtered);
    }
  }, [notes, searchQuery]);

  const loadNotes = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await NotesService.getUserNotes(user.id, {
        limit: 100,
        sort_order: 'DESC'
      });
      setNotes(response.data);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newNoteTitle.trim()) return;

    try {
      setError(null);
      const newNote = await NotesService.createNoteFromTitle(
        user.id,
        newNoteTitle.trim(),
        [] // Empty blocks array for new note
      );
      setNotes([newNote, ...notes]);
      setNewNoteTitle('');
      setShowCreateForm(false);
    } catch (err) {
      console.error('Error creating note:', err);
      setError('Failed to create note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      setError(null);
      await NotesService.deleteNote(noteId);
      setNotes(notes.filter(note => note.note_id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  const handleNoteClick = (noteId: string) => {
    navigate(`/notes/${noteId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="notes-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Notes</h1>}
        />
        <div className="notes-auth-message">
          Please sign in to access your notes.
        </div>
      </div>
    );
  }

  return (
    <div className="notes-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">Notes</h1>}
        rightActions={
          <button 
            className="notes-create-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕' : '+'} {showCreateForm ? 'Cancel' : 'New Note'}
          </button>
        }
      />

      {error && (
        <div className="notes-error">
          {error}
          <button onClick={loadNotes} className="notes-retry-btn">Retry</button>
        </div>
      )}

      {showCreateForm && (
        <div className="notes-create-form">
          <form onSubmit={handleCreateNote}>
            <div className="create-form-header">
              <span className="create-form-title">Create New Note</span>
            </div>
            <div className="create-form-content">
              <input
                type="text"
                placeholder="Enter note title..."
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                className="create-note-input"
                autoFocus
              />
              <div className="create-form-actions">
                <button type="submit" className="create-submit-btn">
                  Create Note
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewNoteTitle('');
                  }}
                  className="create-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="notes-search">
        <input
          type="text"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="notes-search-input"
        />
      </div>

      <div className="notes-content">
        {loading ? (
          <div className="notes-loading">
            <div className="loading-spinner"></div>
            <p>Loading notes...</p>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="notes-empty">
            {searchQuery ? (
              <div>
                <p>No notes found matching "{searchQuery}"</p>
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  Clear search
                </button>
              </div>
            ) : (
              <div>
                <p>You don't have any notes yet.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="create-first-note-btn"
                >
                  Create your first note
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="notes-list">
            {filteredNotes.map((note) => (
              <div key={note.id} className="note-item" onClick={() => handleNoteClick(note.note_id)}>
                <div className="note-item-header">
                  <h3 className="note-item-title">{note.title}</h3>
                  <div className="note-item-actions">

                  </div>
                </div>
                <div className="note-item-meta">
                  <span className="note-item-id">/{note.note_id}</span>
                  <span className="note-item-date">
                    {formatDate(note.updated_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notes;
